import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { authActions } from '../../../../../../state/ducks/auth';
import { useAsyncDispatch } from '../../../../../../state/store';
import staticLinks from '../../data/static-links';
import { HelpAgentHostAdapter } from '../../remote/helpAgentHostAdapter';
import { HelpAgentPanelController } from '../../remote/helpAgentPanelController';
import { helpAgentRemoteLoader } from '../../remote/helpAgentRemoteLoader';
import resolveHelpCenterUrl from '../../utils/resolveHelpCenterUrl';
import useStyles from './HelpAgentPanel.styles';

export interface HelpAgentPanelProps {
  open: boolean
  anchorEl: HTMLButtonElement | null
  onClose: () => void
}

const HelpAgentPanel: React.FC<HelpAgentPanelProps> = ({ open, anchorEl, onClose }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const dispatch = useAsyncDispatch();
  const dispatchRef = useRef(dispatch);
  const callbacksRef = useRef({ anchorEl, onClose });
  const panelRef = useRef<HTMLElement | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const statusRef = useRef<HTMLDivElement | null>(null);
  const controllerRef = useRef<HelpAgentPanelController | null>(null);

  dispatchRef.current = dispatch;
  callbacksRef.current = { anchorEl, onClose };

  useEffect(() => {
    const panel = panelRef.current;
    const mount = mountRef.current;
    const status = statusRef.current;
    if (panel === null || mount === null || status === null) {
      throw new Error('Help Agent panel host was not mounted');
    }

    const adapter = new HelpAgentHostAdapter({
      loader: helpAgentRemoteLoader,
      elements: { mount, status },
      footerLinks: {
        releaseNotes: resolveHelpCenterUrl(staticLinks.ReleaseNotes),
        contact: resolveHelpCenterUrl(staticLinks.Contact),
      },
      callbacks: {
        close: () => callbacksRef.current.onClose(),
        reposition: () => window.dispatchEvent(new Event('resize')),
        logout: () => dispatchRef.current(authActions.logout()),
      },
    });
    const controller = new HelpAgentPanelController({
      getPanel: () => panelRef.current,
      getButton: () => callbacksRef.current.anchorEl,
      onOpen: () => {
        adapter.open().catch(() => undefined);
      },
      onClose: () => {
        adapter.close();
        callbacksRef.current.onClose();
      },
      focusPanelContent: () => adapter.focusPanelContent(),
    });

    controllerRef.current = controller;
    controller.connect();

    return () => {
      controller.disconnect();
      adapter.disconnect();
      controllerRef.current = null;
    };
  }, []);

  useEffect(() => {
    const controller = controllerRef.current;
    if (controller === null) {
      return;
    }
    if (open) {
      controller.open({ restoreFocus: false });
    } else {
      controller.close({ restoreFocus: false });
    }
  }, [open]);

  return ReactDOM.createPortal(
    <section
      ref={panelRef}
      aria-hidden="true"
      aria-label={formatMessage({ id: 'help.ai' })}
      className={classes.panel}
      data-cy="ai-help-panel"
      role="dialog"
    >
      <div
        ref={statusRef}
        aria-live="polite"
        className={`${classes.status} help-agent-panel-status`}
        data-cy="help-agent-panel-status"
        role="status"
      />
      <div ref={mountRef} data-cy="ai-help-mount" />
    </section>,
    document.body,
  );
};

export default HelpAgentPanel;
